// SCR-STAFF-877 (AP-INV-01؛ US-INV-060): تأمین‌کنندگان کالا و خدمات فنی با نام، نوع، شناسه یا کد ملی، کد اقتصادی، تماس، نشانی، شبا، زمان تحویل و
// شرایط پرداخت (AC-INV-060.1)؛ غیرفعال‌سازی به‌جای حذف. AP-INV-03 (فاز 2، feature.inv.inventory): تب‌های پرداختنی، پرداخت و چک، قرارداد تأمین و
// تحلیل قیمت خرید (US-INV-061 تا 064).
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type InvSupplierRequest, type InvSupplierType, type InvSupplierView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  faDigits,
  FormDialog,
  latinDigits,
  ListPage,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { inventoryKeys, SUPPLIER_TYPES } from './inventoryCommon';
import { ContractsTab, PayablesTab, PaymentsTab, PriceAnalysisTab } from './SupplierFinanceTabs';
import { INVENTORY_FEATURE } from './stockCommon';

export const SCR_STAFF_877 = 'SCR-STAFF-877';

type SupplierTab = 'suppliers' | 'payables' | 'payments' | 'contracts' | 'analysis';

export function SuppliersPage() {
  const { t } = useTranslation('inv');
  const access = useAccess();
  const phase2 = access.featureEnabled(INVENTORY_FEATURE);
  const tabs: SupplierTab[] = [
    'suppliers',
    ...(phase2 && access.can('inv.supplier_payment.manage') ? (['payables', 'payments'] as const) : []),
    ...(phase2 && (access.can('inv.supply_contract.manage') || access.can('inv.po.manage')) ? (['contracts'] as const) : []),
    ...(phase2 && access.can('inv.report.view') ? (['analysis'] as const) : []),
  ];
  const [tab, setTab] = useState<SupplierTab>('suppliers');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('inv.supplier.manage');
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [type, setType] = useState<InvSupplierType | ''>('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<InvSupplierView | null | undefined>(undefined);

  const suppliers = useQuery({
    queryKey: [...inventoryKeys.suppliers(session?.tenantId), q, type, cursor],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/inventory/suppliers', { params: { query: { q: q.trim() || undefined, type: type || undefined, cursor, limit: 50 } } })),
  });

  const toggle = useMutation({
    mutationFn: (supplier: InvSupplierView) =>
      unwrap(
        supplier.isActive
          ? tenantApi.POST('/api/v1/inventory/suppliers/{id}:deactivate', { params: { path: { id: supplier.id } } })
          : tenantApi.POST('/api/v1/inventory/suppliers/{id}:activate', { params: { path: { id: supplier.id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: inventoryKeys.suppliers(session?.tenantId) }),
  });

  const columns: DataTableColumn<InvSupplierView>[] = [
    { id: 'name', headerKey: 'inv:suppliers.columns.name', cell: (row) => row.name },
    { id: 'type', headerKey: 'inv:suppliers.columns.type', cell: (row) => t(`inv:supplier_type.${row.type}`) },
    { id: 'nationalId', headerKey: 'inv:suppliers.columns.national_id', cell: (row) => <bdi dir="ltr">{row.nationalId ?? '—'}</bdi> },
    { id: 'contact', headerKey: 'inv:suppliers.columns.contact', cell: (row) => [row.contactPerson, row.mobile ?? row.phone].filter(Boolean).join(' · ') || '—' },
    {
      id: 'leadTime',
      headerKey: 'inv:suppliers.columns.lead_time',
      cell: (row) => (row.leadTimeDays === null || row.leadTimeDays === undefined ? '—' : t('inv:suppliers.days', { value: faDigits(Number(row.leadTimeDays)) })),
    },
    {
      id: 'status',
      headerKey: 'inv:suppliers.columns.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} labelKey={row.isActive ? 'inv:status.active' : 'inv:status.inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'inv:suppliers.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('inv:suppliers.edit')}>
            <span>
              <IconButton aria-label={`${t('inv:suppliers.edit')} ${row.name}`} onClick={() => setEditing(row)} disabled={!write.canWrite} data-testid={`supplier-edit-${row.id}`}>
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => toggle.mutate(row)} disabled={!write.canWrite || toggle.isPending}>
            {t(row.isActive ? 'inv:suppliers.deactivate' : 'inv:suppliers.activate')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_877}
      titleKey="inv:suppliers.title"
      subtitleKey="inv:suppliers.subtitle"
      actions={
        tab === 'suppliers' ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(null)} disabled={!write.canWrite} data-testid="supplier-create">
            {t('inv:suppliers.add')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack spacing={1}>
          {tabs.length > 1 && (
            <Tabs value={tab} onChange={(_event, value: SupplierTab) => setTab(value)} aria-label={t('inv:suppliers.title')} variant="scrollable">
              {tabs.map((value) => (
                <Tab key={value} value={value} label={t(`inv:suppliers.tabs.${value}`)} data-testid={`suppliers-tab-${value}`} />
              ))}
            </Tabs>
          )}
          {tab === 'suppliers' && <WriteNotice write={write} />}
          {tab === 'suppliers' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                label={t('inv:suppliers.search')}
                value={q}
                onChange={(event) => {
                  setCursor(undefined);
                  setQ(event.target.value);
                }}
                slotProps={{ htmlInput: { 'data-testid': 'supplier-search' } }}
                sx={{ minWidth: 240 }}
              />
              <TextField
                select
                size="small"
                label={t('inv:suppliers.filters.type')}
                value={type}
                onChange={(event) => {
                  setCursor(undefined);
                  setType(event.target.value as InvSupplierType | '');
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
                {SUPPLIER_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`inv:supplier_type.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </Stack>
      }
    >
      {tab === 'payables' && <PayablesTab />}
      {tab === 'payments' && <PaymentsTab />}
      {tab === 'contracts' && <ContractsTab />}
      {tab === 'analysis' && <PriceAnalysisTab />}
      {tab === 'suppliers' && (
        <DataTable
          captionKey="inv:suppliers.caption"
          columns={columns}
          rows={suppliers.data?.items}
          getRowId={(row) => row.id}
          isLoading={suppliers.isPending}
          error={suppliers.error}
          onRetry={() => void suppliers.refetch()}
          hasMore={Boolean(suppliers.data?.nextCursor)}
          onLoadMore={() => setCursor(suppliers.data?.nextCursor ?? undefined)}
          emptyTitleKey="inv:suppliers.empty"
        />
      )}
      {editing !== undefined && <SupplierDialog supplier={editing} onClose={() => setEditing(undefined)} />}
    </ListPage>
  );
}

const optionalDigits = (pattern: RegExp) =>
  z.string().refine((value) => !value.trim() || pattern.test(latinDigits(value).replace(/[\s-]/g, '')), { error: 'common:validation.invalid' });

const supplierSchema = z.object({
  name: zRequiredText(200),
  type: z.enum(SUPPLIER_TYPES as [InvSupplierType, ...InvSupplierType[]]),
  nationalId: optionalDigits(/^[0-9]{8,11}$/),
  economicCode: optionalDigits(/^([0-9]{12}|[0-9]{14})$/),
  contactPerson: z.string().max(120, { error: 'common:validation.too_long' }),
  phone: z.string().max(20, { error: 'common:validation.too_long' }),
  mobile: z.string(),
  email: z.string().max(120, { error: 'common:validation.too_long' }),
  address: z.string().max(500, { error: 'common:validation.too_long' }),
  iban: z.string(),
  leadTimeDays: optionalDigits(/^[0-9]{1,3}$/),
  paymentTermDays: optionalDigits(/^[0-9]{1,3}$/),
  paymentTerms: z.string().max(500, { error: 'common:validation.too_long' }),
});
type SupplierForm = z.input<typeof supplierSchema>;

function toRequest(form: SupplierForm, rowVersion?: number | string): InvSupplierRequest {
  const text = (value: string) => (value.trim() ? latinDigits(value.trim()) : null);
  const number = (value: string) => (value.trim() ? Number(latinDigits(value.trim())) : null);
  return {
    name: form.name.trim(),
    type: form.type,
    nationalId: text(form.nationalId),
    economicCode: text(form.economicCode),
    contactPerson: form.contactPerson.trim() || null,
    phone: text(form.phone),
    mobile: text(form.mobile),
    email: form.email.trim() || null,
    address: form.address.trim() || null,
    iban: text(form.iban),
    leadTimeDays: number(form.leadTimeDays),
    paymentTermDays: number(form.paymentTermDays),
    paymentTerms: form.paymentTerms.trim() || null,
    ...(rowVersion === undefined ? {} : { rowVersion: Number(rowVersion) }),
  };
}

function SupplierDialog({ supplier, onClose }: { supplier: InvSupplierView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = supplier === null;
  const optional = (value: number | string | null | undefined) => (value === null || value === undefined ? '' : String(value));
  const { control, handleSubmit, formState, setError } = useForm<SupplierForm>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: supplier?.name ?? '',
      type: supplier?.type ?? 'Goods',
      nationalId: supplier?.nationalId ?? '',
      economicCode: supplier?.economicCode ?? '',
      contactPerson: supplier?.contactPerson ?? '',
      phone: supplier?.phone ?? '',
      mobile: supplier?.mobile ?? '',
      email: supplier?.email ?? '',
      address: supplier?.address ?? '',
      iban: supplier?.iban ?? '',
      leadTimeDays: optional(supplier?.leadTimeDays),
      paymentTermDays: optional(supplier?.paymentTermDays),
      paymentTerms: supplier?.paymentTerms ?? '',
    },
  });

  const save = useMutation({
    mutationFn: (form: SupplierForm) =>
      creating
        ? unwrap(tenantApi.POST('/api/v1/inventory/suppliers', { body: toRequest(form), headers: { 'Idempotency-Key': idempotency.current() } }))
        : unwrap(tenantApi.PUT('/api/v1/inventory/suppliers/{id}', { params: { path: { id: supplier.id } }, body: toRequest(form, supplier.rowVersion) })),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.suppliers(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['name', 'type', 'nationalId', 'economicCode', 'contactPerson', 'phone', 'mobile', 'email', 'address', 'iban', 'leadTimeDays', 'paymentTermDays', 'paymentTerms'],
        codeFields: { 'PHX-INV-0114': 'nationalId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-877-SUPPLIER-FORM"
      titleKey={creating ? 'inv:supplier_form.create_title' : 'inv:supplier_form.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="name" labelKey="inv:supplier_form.name" required fullWidth autoFocus slotProps={{ htmlInput: { 'data-testid': 'supplier-name' } }} />
        <SelectRHF control={control} name="type" labelKey="inv:supplier_form.type" required fullWidth options={SUPPLIER_TYPES.map((value) => ({ value, labelKey: `inv:supplier_type.${value}` }))} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="nationalId" labelKey="inv:supplier_form.national_id" helperKey="inv:supplier_form.national_id_help" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'supplier-national-id' } }} />
        <TextFieldRHF control={control} name="economicCode" labelKey="inv:supplier_form.economic_code" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="contactPerson" labelKey="inv:supplier_form.contact_person" fullWidth />
        <TextFieldRHF control={control} name="mobile" labelKey="inv:supplier_form.mobile" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel' } }} />
        <TextFieldRHF control={control} name="phone" labelKey="inv:supplier_form.phone" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="email" labelKey="inv:supplier_form.email" fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />
        <TextFieldRHF control={control} name="iban" labelKey="inv:supplier_form.iban" fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'supplier-iban' } }} />
      </Stack>
      <TextFieldRHF control={control} name="address" labelKey="inv:supplier_form.address" fullWidth />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="leadTimeDays" labelKey="inv:supplier_form.lead_time_days" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'supplier-lead-time' } }} />
        <TextFieldRHF control={control} name="paymentTermDays" labelKey="inv:supplier_form.payment_term_days" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
      </Stack>
      <TextFieldRHF control={control} name="paymentTerms" labelKey="inv:supplier_form.payment_terms" fullWidth />
    </FormDialog>
  );
}
